import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getUserMe, getMyTeam, updateUserProfile, deleteAccount, getInstallationSettingsUrl } from "../api";
import AppHeader from "../components/AppHeader";
import ConfirmDialog from "../components/ConfirmDialog";
import { formatPeriodEnd } from "../utils/planStatus";

function ProfilePage() {
  const { user, logout, updateProfileFields } = useAuth();
  const [profile, setProfile] = useState(null);
  // Only for AppHeader's plan badge. Every other app page passes this;
  // without it the trial/Pro/Free pill silently vanishes on /profile and
  // reappears when you navigate away.
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [displayNameInput, setDisplayNameInput] = useState("");
  const [contactEmailInput, setContactEmailInput] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fresh from the server, not just AuthContext's cached copy — another
  // session (or the Stripe checkout capture) may have changed contact_email
  // since this one last logged in.
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // In parallel — the team fetch only feeds the header's plan badge, so
      // a failure there must not block or fail the profile itself.
      const [res, teamRes] = await Promise.all([
        getUserMe(),
        getMyTeam().catch(() => null),
      ]);
      if (teamRes) setTeam(teamRes.data?.data?.team || null);
      const u = res.data?.data?.user;
      setProfile(u || null);
      setDisplayNameInput(u?.display_name || "");
      setContactEmailInput(u?.contact_email || "");
      // Reconcile AuthContext's cached copy with what the server actually
      // has, not just on save. Without this, a contact_email set elsewhere
      // (the Stripe checkout capture, or another browser) would leave this
      // session's cached user stale — so you'd see your email filled in
      // here, then go back to the Dashboard and still be told to add one.
      if (u) {
        updateProfileFields({ display_name: u.display_name, contact_email: u.contact_email });
      }
    } catch (err) {
      setError("Failed to load profile: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [updateProfileFields]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Deliberately no `hasTeam === false` redirect here, unlike TeamPage and
  // AnalyticsPage. Those genuinely need a team; a profile always exists and
  // is always editable — and a teamless user must still be able to delete
  // their account (case A), which that redirect made unreachable.

  const handleSave = async (e) => {
    e.preventDefault();
    setActionError(null);
    setSaved(false);
    const trimmedName = displayNameInput.trim();
    if (!trimmedName) {
      setActionError("Display name can't be empty");
      return;
    }
    setSaving(true);
    try {
      const res = await updateUserProfile({
        display_name: trimmedName,
        contact_email: contactEmailInput.trim(),
      });
      const u = res.data?.data?.user;
      setProfile(u);
      setDisplayNameInput(u?.display_name || "");
      setContactEmailInput(u?.contact_email || "");
      updateProfileFields({ display_name: u?.display_name, contact_email: u?.contact_email });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setActionError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setActionError(null);
    setDeleting(true);
    try {
      const res = await deleteAccount();
      // app_uninstalled is null when no team was deleted, true on success, and
      // false only when the team went but the GitHub App is still installed.
      // The confirmation dialog promises the App is removed, so when that
      // fails we have to say so — but this flow logs straight out, leaving
      // nowhere on this page to put it. Carry a flag through the redirect and
      // let /login surface the manual settings link.
      //
      // A flag, not the installation id: the id would ride in a URL and land
      // in 30-day CloudFront access logs, and the generic installations page
      // is enough to find it.
      const data = res.data?.data || {};
      const appKept = data.app_uninstalled === false;
      logout(`/login?reason=deleted${appKept ? "&app=kept" : ""}`, { hard: true });
    } catch (err) {
      const status = err.response?.status;
      // 404 means the account is already gone — the delete committed and the
      // response was lost (Lambda timeout, dropped connection). Retrying
      // succeeded from the user's point of view, so treat it as success
      // rather than showing a scary error for something that worked.
      if (status === 404) {
        logout("/login?reason=deleted", { hard: true });
        return;
      }
      setDeleting(false);
      setConfirmingDelete(false);
      // 502 is the one case where being explicit really matters: "an error
      // occurred" right after clicking Delete is genuinely frightening when
      // the truth is that nothing happened.
      setActionError(
        status === 502
          ? "We couldn't cancel your subscription, so nothing was deleted. Please try again."
          : err.response?.data?.error || err.message
      );
    }
  };

  if (loading && !profile) {
    return (
      <div className="min-h-screen bg-rf-bg-primary flex items-center justify-center">
        <div className="text-rf-text-secondary text-sm">Loading profile...</div>
      </div>
    );
  }

  // Case detection mirrors the server's, so the UI can explain what will
  // happen before the click instead of after. This is a courtesy only — the
  // server re-derives all of it under a row lock and is the real boundary.
  // Don't "simplify" by trusting one side alone.
  const members = team?.members || [];
  const soleMember = !!team && members.length === 1;
  const lastAdmin =
    !!team && members.length > 1 && members.filter((m) => m.team_role === "admin").length === 1
    && members.find((m) => m.user_id === profile?.user_id)?.team_role === "admin";
  const periodEnd = formatPeriodEnd(team?.plan, { long: true });
  // Warn about forfeited paid time whenever a subscription is still running,
  // including one already set to cancel at period end — that user
  // deliberately arranged to keep access until a date, and deleting now
  // takes it away.
  const forfeitsPaidTime = soleMember && !!team?.stripe_subscription_id && !!periodEnd;

  const initials = (profile?.display_name || profile?.github_username || "??")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-rf-bg-primary">
      <AppHeader user={user} onLogout={logout} plan={team?.plan} active="profile" />

      <main className="max-w-[1200px] mx-auto px-8 py-7">
        <div className="mb-7">
          <h1 className="text-2xl font-bold text-rf-text-primary tracking-tight mb-1">My Profile</h1>
          <p className="text-sm text-rf-text-secondary">Manage how you appear and how we reach you.</p>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-rf-accent-danger/10 border border-rf-accent-danger/30 text-rf-accent-danger text-sm">
            {error}
            <button onClick={fetchData} className="ml-3 underline hover:no-underline">
              Retry
            </button>
          </div>
        )}

        <div className="bg-rf-bg-secondary rounded-xl p-6 mb-6">
          <h2 className="text-base font-semibold text-rf-text-primary mb-4">GitHub Identity</h2>
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-full bg-rf-accent-blue/15 flex items-center justify-center text-sm text-rf-accent-blue font-semibold shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-rf-text-primary">@{profile?.github_username}</div>
              <div className="text-[11px] text-rf-text-secondary">Signed in via GitHub</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-rf-border/40">
            <div className="text-xs text-rf-text-muted uppercase tracking-wider mb-1">GitHub email</div>
            <div className="text-sm text-rf-text-secondary">
              {profile?.email || "Not public on your GitHub profile"}
            </div>
          </div>
        </div>

        <div className="bg-rf-bg-secondary rounded-xl p-6">
          <h2 className="text-base font-semibold text-rf-text-primary mb-4">Your Details</h2>

          {actionError && (
            <div className="mb-4 p-4 rounded-lg bg-rf-accent-danger/10 border border-rf-accent-danger/30 text-rf-accent-danger text-sm">
              {actionError}
            </div>
          )}

          {/* noValidate: the server is the single source of truth for
              validation errors (same inline regex the Lambda uses) — without
              this, the browser's native email-format check silently blocks
              submission before our own error message ever has a chance to
              show. */}
          <form onSubmit={handleSave} className="space-y-4" noValidate>
            <div>
              <label htmlFor="profile-display-name" className="block text-xs text-rf-text-secondary mb-1.5 uppercase tracking-wider">
                Display Name
              </label>
              <input
                id="profile-display-name"
                type="text"
                value={displayNameInput}
                onChange={(e) => setDisplayNameInput(e.target.value)}
                placeholder="Your name"
                className="w-full px-3.5 py-2.5 rounded-lg border border-rf-border bg-rf-bg-tertiary text-rf-text-primary text-sm outline-none focus:border-rf-accent-blue/50"
                required
              />
            </div>
            <div>
              <label htmlFor="profile-contact-email" className="block text-xs text-rf-text-secondary mb-1.5 uppercase tracking-wider">
                Contact Email
              </label>
              <input
                id="profile-contact-email"
                type="email"
                value={contactEmailInput}
                onChange={(e) => setContactEmailInput(e.target.value)}
                placeholder="you@company.com"
                className="w-full px-3.5 py-2.5 rounded-lg border border-rf-border bg-rf-bg-tertiary text-rf-text-primary text-sm outline-none focus:border-rf-accent-blue/50"
              />
              <p className="text-xs text-rf-text-muted mt-1.5">
                Used for account and billing notices. Never shown to your teammates.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2.5 rounded-lg bg-rf-accent text-white font-semibold text-sm hover:bg-rf-accent/90 transition-colors disabled:opacity-50 disabled:pointer-events-none"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              {saved && <span className="text-sm text-rf-accent">Saved</span>}
            </div>
          </form>
        </div>

        {/* Danger zone. Bottom of the page, visually separated, never in the
            header menu — this is not something to stumble into. Copy states
            only what the code actually does: it never claims the GitHub App
            is removed (we have no API to uninstall it) and never claims more
            erasure than the scrub delivers. */}
        <div className="bg-rf-bg-secondary rounded-xl p-6 mt-6 border border-rf-accent-danger/30">
          <h2 className="text-base font-semibold text-rf-accent-danger mb-4">Delete account</h2>

          {lastAdmin ? (
            <>
              <p className="text-sm text-rf-text-secondary mb-4">
                You're the only admin on {team?.team_name}. Promote another teammate to admin
                before you can delete your account.
              </p>
              <Link
                to="/team"
                className="inline-block px-4 py-2 rounded-md border border-rf-border text-rf-text-secondary text-sm font-medium hover:text-rf-text-primary transition-colors"
              >
                Go to Team →
              </Link>
            </>
          ) : (
            <>
              {soleMember ? (
                <>
                  <p className="text-sm text-rf-text-secondary mb-4">
                    You're the only member of {team?.team_name}. Deleting your account also deletes
                    the team and everything in it — every pull request record, every review
                    assignment, and all team analytics. Nothing is removed straight away — you
                    have 30 days to restore everything by signing back in.
                  </p>
                  {/*
                    Second irreversible consequence, stated plainly rather than
                    left to a type-to-confirm to cover implicitly. Only shown in
                    the sole-member branch because only that branch deletes the
                    team, and only a team deletion uninstalls the App — the
                    ordinary-member branch below leaves both standing.
                  */}
                  {team?.github_installation_id && (
                    <p className="text-sm text-rf-text-secondary mb-4">
                      This also removes the Anthurium GitHub App from your organization, so we lose
                      access to those repositories. You can install it again later if you start a
                      new team.
                    </p>
                  )}
                  {/*
                    Account deletion used to be the only way to get rid of a
                    team, which made this section the destination for two quite
                    different intentions. DELETE /api/teams/me now serves the
                    smaller one, so offer it here rather than letting someone
                    erase themselves to achieve it.
                  */}
                  <p className="text-sm text-rf-text-secondary mb-4">
                    If you only want to get rid of the team,{" "}
                    <Link
                      to="/team"
                      className="text-rf-accent-blue hover:text-rf-text-primary transition-colors"
                    >
                      delete the team instead
                    </Link>{" "}
                    — that keeps your profile and lets you join or start another one.
                  </p>
                </>
              ) : (
                <p className="text-sm text-rf-text-secondary mb-4">
                  Deleting your account removes your profile, your review assignments, and the
                  file-history data used to route pull requests to you. Nothing is removed
                  straight away — you have 30 days to restore it by signing back in.
                  {team && " Your team keeps its pull request history — PRs you opened stay on the dashboard with the author shown as [deleted], and anything assigned to you is handed to another reviewer."}
                </p>
              )}

              {forfeitsPaidTime && (
                <p className="text-sm text-rf-accent-warning mb-4">
                  You have Pro access until {periodEnd}. Deleting now cancels immediately and
                  forfeits the rest of that period — you won't be refunded.
                </p>
              )}

              {team?.github_installation_id && (
                <p className="text-xs text-rf-text-muted mb-4">
                  Anthurium's GitHub App stays installed. If you're an organization owner, you can{" "}
                  <a
                    href={getInstallationSettingsUrl(team.github_installation_id)}
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-rf-text-secondary"
                  >
                    remove it from GitHub
                  </a>{" "}
                  to fully disconnect your repositories.
                </p>
              )}

              <button
                onClick={() => { setActionError(null); setConfirmingDelete(true); }}
                className="px-4 py-2 rounded-md border border-rf-accent-danger/30 bg-rf-accent-danger/10 text-rf-accent-danger text-sm font-medium hover:bg-rf-accent-danger/20 transition-colors"
              >
                {soleMember ? "Delete account and team" : "Delete account"}
              </button>
            </>
          )}
        </div>
      </main>

      <ConfirmDialog
        open={confirmingDelete}
        danger
        busy={deleting}
        busyLabel="Deleting…"
        title={soleMember ? `Delete your account and ${team?.team_name}?` : "Delete your account?"}
        // Type-to-confirm only for the case that destroys a whole team's
        // history and cancels a paid subscription — the same bar GitHub and
        // Stripe set. A single account deletion doesn't warrant it.
        confirmationPhrase={soleMember ? team?.team_name : null}
        confirmLabel={soleMember ? "Delete everything" : "Delete account"}
        message={
          <>
            {soleMember ? (
              <p className="mb-3">
                This deletes your account and everything belonging to {team?.team_name} — pull
                request history, review assignments, and analytics.
              </p>
            ) : (
              <p className="mb-3">
                Your profile, review assignments, and file-history data will be deleted. Pull
                requests you opened stay in your team's history with the author anonymized.
              </p>
            )}
            {forfeitsPaidTime && (
              <p className="mb-3 text-rf-accent-warning">
                Your subscription will be set to cancel at the end of the period you've already
                paid for ({periodEnd}), and won't renew. Restoring within 30 days resumes it.
              </p>
            )}
            <p>
              You have <span className="text-rf-text-primary">30 days</span> to change your mind —
              signing back in with GitHub restores your account. After that it is permanently
              deleted.
            </p>
          </>
        }
        onConfirm={handleDelete}
        onCancel={() => setConfirmingDelete(false)}
      />
    </div>
  );
}

export default ProfilePage;
